import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import {
  Plus, Minus, ShoppingBag, Search, X, User,
  LayoutGrid, CalendarDays, RefreshCw, CheckSquare,
} from 'lucide-react';
import { todayIST, currentISTHour, fmtDateIST } from '../../utils/ist';

const WD_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function generateBookingDates(from, to, mode, weekdaySet) {
  if (mode === 'single' || !to || to < from) return [from];
  const dates = [];
  const cur = new Date(from + 'T12:00:00');
  const end = new Date(to + 'T12:00:00');
  while (cur <= end) {
    if (mode !== 'recurring' || weekdaySet.has(cur.getDay())) {
      dates.push(cur.toISOString().split('T')[0]);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

const fmtDate = fmtDateIST;

// Recurring flow — time-of-day windows (reference page-new-booking.jsx §TIME_WINDOWS)
const TIME_WINDOWS = {
  morning:   { label: 'Morning',   range: '6 AM – 12 PM', hours: [6, 7, 8, 9, 10, 11] },
  afternoon: { label: 'Afternoon', range: '12 – 5 PM',    hours: [12, 13, 14, 15, 16] },
  evening:   { label: 'Evening',   range: '5 – 10 PM',    hours: [17, 18, 19, 20, 21] },
};
const periodForHour = (h) => (h >= 17 ? 'evening' : h >= 12 ? 'afternoon' : 'morning');
// Start hour from a slot_code ("0600-0730" → 6) — for past-checks and time-of-day bucketing.
const slotStartHour = (slotId) => parseInt(String(slotId).slice(0, 2), 10);
const daysBetweenISO = (a, b) => Math.round((new Date(b + 'T00:00') - new Date(a + 'T00:00')) / 86400000);
const chunk = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

/* Inline style helpers that map to sb-* tokens */
const s = {
  card:      { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', padding: '20px 22px' },
  cardHead:  { borderBottom: '1px solid var(--line-soft)', paddingBottom: 14, marginBottom: 16 },
  sec:       { fontSize: 14, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--ink)', margin: 0 },
  label:     { fontSize: 12, color: 'var(--ink-2)', fontWeight: 500, display: 'block', marginBottom: 6 },
  inp:       {
    background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8,
    padding: '9px 12px', fontSize: 13.5, outline: 'none', width: '100%', color: 'var(--ink)',
    transition: 'border-color .12s',
  },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  accent:    { color: 'var(--accent)' },
  brand:     { color: 'var(--brand)' },
  ink3:      { color: 'var(--ink-3)' },
  ink4:      { color: 'var(--ink-4)' },
  mono:      { fontFamily: 'var(--f-mono)' },
};

export default function AdminCreateBooking() {
  const { user, api } = useAuth();
  const navigate      = useNavigate();
  const routeState    = useLocation().state || {};

  // ── Facilities ───────────────────────────────────────────────────────────
  const { facilities } = useFacilities();
  const allowedLocations = (user?.role === 'owner'
    ? facilities
    : facilities.filter(f => user?.assigned_locations?.includes(f.location_id))
  ).map(f => ({ id: f.location_id, name: f.name }));

  const [form, setForm] = useState({
    customer_name:  '',
    customer_phone: '',
    notes:          '',
    location_id:    routeState.locationId ?? null,
    date:           routeState.date || todayIST(),
  });

  useEffect(() => {
    if (allowedLocations.length > 0 && form.location_id === null) {
      setForm(f => ({ ...f, location_id: allowedLocations[0].id }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedLocations.length]);

  const selectedFacility = facilities.find(f => f.location_id === form.location_id);
  const locationGrounds  = selectedFacility?.grounds || [];
  const rentalCatalogue  = selectedFacility?.rental_items || [];

  // ── Booking mode ─────────────────────────────────────────────────────────
  const [bookingMode, setBookingMode] = useState('single');
  const today = todayIST();
  const [dateTo,   setDateTo]   = useState(today);
  const [weekdays, setWeekdays] = useState(new Set([0,1,2,3,4,5,6]));
  const toggleWeekday = (d) => setWeekdays(prev => { const n=new Set(prev); n.has(d)?n.delete(d):n.add(d); return n; });

  // ── Recurring (4-step gated) flow ──────────────────────────────────────────
  const [timeWindows, setTimeWindows] = useState(['evening']);
  const toggleWindow = (w) => setTimeWindows(arr => arr.includes(w) ? arr.filter(x=>x!==w) : [...arr, w]);
  const [recSelected, setRecSelected] = useState({}); // `${groundId}|${hour}` -> true
  const toggleRecSlot = (key) => setRecSelected(prev => { const n={...prev}; n[key]?delete n[key]:n[key]=true; return n; });
  const [recAvail, setRecAvail] = useState({ total: 0, occurrences: [], booked: new Set() });
  const [recLoading, setRecLoading] = useState(false);
  const [showFreeOnly, setShowFreeOnly] = useState(true);

  const bookingDates = useMemo(
    () => generateBookingDates(form.date, dateTo, bookingMode, weekdays),
    [form.date, dateTo, bookingMode, weekdays]
  );

  const exceedsLimit = bookingMode === 'recurring' && form.date && dateTo && dateTo >= form.date
    && daysBetweenISO(form.date, dateTo) > 365;

  // Fetch real availability across all occurrences (replaces mock fakeConflicts)
  useEffect(() => {
    if (bookingMode !== 'recurring') return;
    if (!form.location_id || !form.date || !dateTo || dateTo < form.date
        || weekdays.size === 0 || timeWindows.length === 0 || exceedsLimit) {
      setRecAvail({ total: 0, occurrences: [], booked: new Set() });
      return;
    }
    setRecLoading(true);
    api.get('/api/slots/recurring-availability', {
      params: {
        location_id: form.location_id, date_from: form.date, date_to: dateTo,
        weekdays: [...weekdays].sort().join(','),
      },
    })
      .then(res => setRecAvail({
        total: res.data.total || 0,
        occurrences: res.data.occurrences || [],
        booked: new Set(res.data.booked || []),
      }))
      .catch(() => { setRecAvail({ total: 0, occurrences: [], booked: new Set() }); toast.error('Failed to load availability'); })
      .finally(() => setRecLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingMode, form.location_id, form.date, dateTo, weekdays, timeWindows, exceedsLimit]);

  // Each ground's own custom slots whose start hour falls in a selected window.
  const recurringSlots = useMemo(() => {
    if (bookingMode !== 'recurring' || recAvail.occurrences.length === 0 || timeWindows.length === 0) return [];
    const list = [];
    for (const ground of locationGrounds) {
      for (const slot of (ground.slots || [])) {
        if (slot.active === false) continue;
        const startH = slotStartHour(slot.start_time?.replace(':', '') ?? slot.slot_code);
        if (!timeWindows.includes(periodForHour(startH))) continue;
        const sid = slot.slot_code;
        const free = recAvail.occurrences.filter(d => !recAvail.booked.has(`${d}|${ground.id}|${sid}`)).length;
        list.push({
          ground, sid, key: `${ground.id}|${sid}`,
          label: slot.label, price: slot.price || 0, start_time: slot.start_time,
          free, total: recAvail.occurrences.length,
        });
      }
    }
    list.sort((a, b) => (b.free - a.free) || (a.start_time || '').localeCompare(b.start_time || ''));
    return list;
  }, [bookingMode, locationGrounds, timeWindows, recAvail]);

  const recSessionCount  = Object.keys(recSelected).reduce((s2, k) => s2 + (recurringSlots.find(x => x.key === k)?.free || 0), 0);
  const recCourtSubtotal = Object.keys(recSelected).reduce((s2, k) => { const sl = recurringSlots.find(x => x.key === k); return sl ? s2 + sl.price * sl.free : s2; }, 0);
  const recReady = bookingMode === 'recurring' && !exceedsLimit && weekdays.size > 0 && timeWindows.length > 0 && recAvail.occurrences.length > 0;
  const visibleRecSlots = showFreeOnly ? recurringSlots.filter(sl => sl.free > 0) : recurringSlots;
  const fullyFreeCount  = recurringSlots.filter(sl => sl.free === sl.total).length;

  // ── Slot grid ────────────────────────────────────────────────────────────
  const [slotsByGround, setSlotsByGround]  = useState({});
  const [rentalUsage,   setRentalUsage]    = useState({});
  const [slotsLoading,  setSlotsLoading]   = useState(false);
  const [selectedCells, setSelectedCells] = useState(new Set());

  // Pre-select from calendar — supports a single slot (groundId/slotId) or
  // multiple cells ({ groundId, slotId }[]) selected in the Courts grid.
  const preCells = routeState.cells?.length
    ? routeState.cells
    : (routeState.groundId && routeState.slotId ? [{ groundId: routeState.groundId, slotId: routeState.slotId }] : []);

  const fetchSlots = useCallback(() => {
    if (!form.location_id || !form.date || locationGrounds.length === 0) return;
    setSlotsLoading(true);
    api.get('/api/slots/location-availability', {
      params: { location_id: form.location_id, date: form.date },
    })
      .then(res => {
        setSlotsByGround(res.data.slots || {});
        setRentalUsage(res.data.rental_usage || {});
      })
      .catch(() => toast.error('Failed to load slots'))
      .finally(() => setSlotsLoading(false));
  }, [api, form.location_id, form.date, locationGrounds.length]);

  useEffect(() => { setSelectedCells(new Set()); fetchSlots(); }, [fetchSlots]);

  // Auto-select pre-fill from calendar (all available pre-cells)
  useEffect(() => {
    if (preCells.length === 0 || Object.keys(slotsByGround).length === 0) return;
    const keys = preCells
      .filter(c => (slotsByGround[c.groundId] || []).find(s => s.id === c.slotId)?.available)
      .map(c => `${c.groundId}::${c.slotId}`);
    if (keys.length > 0) setSelectedCells(new Set(keys));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsByGround]);

  const isSlotPast = useCallback((slotId) => {
    if (form.date > today) return false;
    if (form.date < today) return true;
    return slotStartHour(slotId) < currentISTHour();
  }, [form.date, today]);

  const toggleCell = (groundId, slotId) =>
    setSelectedCells(prev => { const n=new Set(prev); const k=`${groundId}::${slotId}`; n.has(k)?n.delete(k):n.add(k); return n; });

  const toggleRow = (slotId) => {
    if (isSlotPast(slotId)) return;
    const avail = locationGrounds
      .filter(g => (slotsByGround[g.id]||[]).find(s=>s.id===slotId)?.available)
      .map(g => `${g.id}::${slotId}`);
    const allOn = avail.every(k => selectedCells.has(k));
    setSelectedCells(prev => { const n=new Set(prev); avail.forEach(k=>allOn?n.delete(k):n.add(k)); return n; });
  };

  const toggleColumn = (groundId) => {
    const avail = (slotsByGround[groundId]||[])
      .filter(s=>s.available && !isSlotPast(s.id)).map(s=>`${groundId}::${s.id}`);
    const allOn = avail.every(k=>selectedCells.has(k));
    setSelectedCells(prev => { const n=new Set(prev); avail.forEach(k=>allOn?n.delete(k):n.add(k)); return n; });
  };

  const allAvailableKeys = Object.entries(slotsByGround).flatMap(([gid, slots]) =>
    slots.filter(s => s.available && !isSlotPast(s.id)).map(s => `${gid}::${s.id}`)
  );
  const allAvailableSelected = allAvailableKeys.length > 0 && allAvailableKeys.every(k=>selectedCells.has(k));
  const toggleAll = () => setSelectedCells(allAvailableSelected ? new Set() : new Set(allAvailableKeys));

  // Union of every ground's slots (grounds may have different custom slot lists),
  // de-duped by slot_code and ordered by start time → the grid's row template.
  const allTimeSlots = useMemo(() => {
    const byCode = new Map();
    for (const slots of Object.values(slotsByGround)) {
      for (const sl of (slots || [])) if (!byCode.has(sl.id)) byCode.set(sl.id, sl);
    }
    return [...byCode.values()].sort((a, b) => (a.start_time || a.id).localeCompare(b.start_time || b.id));
  }, [slotsByGround]);
  const totalBookings = selectedCells.size;
  const selectionByGround = {};
  for (const key of selectedCells) {
    const [gid, sid] = key.split('::');
    if (!selectionByGround[gid]) selectionByGround[gid] = [];
    selectionByGround[gid].push(sid);
  }
  const grandTotal = totalBookings * bookingDates.length;

  // ── Customer search ──────────────────────────────────────────────────────
  const [searchQuery,     setSearchQuery]     = useState('');
  const [suggestions,     setSuggestions]     = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchQuery.length < 3) { setSuggestions([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(() => {
      api.get('/api/bookings/customers/search', { params: { q: searchQuery } })
        .then(r => { setSuggestions(r.data); setShowSuggestions(true); })
        .catch(() => {})
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => { clearTimeout(t); setSearchLoading(false); };
  }, [searchQuery, api]);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pickCustomer = (c) => {
    setForm(f => ({ ...f, customer_name: c.name, customer_phone: c.phone }));
    setSearchQuery(''); setSuggestions([]); setShowSuggestions(false);
  };

  // ── Rental helpers ───────────────────────────────────────────────────────
  const uniqueSlotIds  = [...new Set([...selectedCells].map(k=>k.split('::')[1]))];
  const rentalHours    = uniqueSlotIds.length;
  const allUsedSlotIds = Object.keys(rentalUsage);
  const alreadyUsed    = (itemId) => {
    const slotIds = uniqueSlotIds.length > 0 ? uniqueSlotIds : allUsedSlotIds;
    if (!slotIds.length) return 0;
    const id = String(itemId);
    return Math.max(...slotIds.map(sid => { const b=rentalUsage[sid]||{}; return b[id]??b[itemId]??0; }));
  };
  const effectiveStock = (item) => Math.max(0, (item.quantity??99) - alreadyUsed(item.item_id));

  const [rentalQty,    setRentalQty]    = useState({});
  const changeRentalQty = (itemId, delta, maxQty) =>
    setRentalQty(prev => ({ ...prev, [itemId]: Math.min(maxQty??99, Math.max(0, (prev[itemId]||0)+delta)) }));

  const [customRentals, setCustomRentals] = useState([]);
  const addCustomRental    = () => setCustomRentals(p => [...p, { id: Date.now(), name: '', price: '', qty: 1 }]);
  const updateCustomRental = (id, f, v) => setCustomRentals(p => p.map(r => r.id===id ? {...r,[f]:v} : r));
  const removeCustomRental = (id) => setCustomRentals(p => p.filter(r => r.id!==id));

  const hrs          = Math.max(1, rentalHours);
  const catalogueTotal = rentalCatalogue.reduce((s,item) => s+(rentalQty[item.item_id]||0)*item.price_per_session*hrs, 0);
  const customTotal    = customRentals.reduce((s,r) => s+(r.qty||1)*(parseFloat(r.price)||0)*hrs, 0);
  const rentalTotal    = catalogueTotal + customTotal;

  // ── Slot pricing ─────────────────────────────────────────────────────────
  // Each custom slot carries its own price (from availability), so read it directly.
  const getSlotPrice = useCallback((groundId, slotId) => {
    const slot = (slotsByGround[groundId] || []).find(s => s.id === slotId);
    return slot?.price || 0;
  }, [slotsByGround]);

  const courtTotal = useMemo(() => {
    let total = 0;
    for (const key of selectedCells) { const [gid,sid]=key.split('::'); total+=getSlotPrice(gid,sid); }
    return total;
  }, [selectedCells, getSlotPrice]);

  // Per-ground court subtotal (slot count + summed price) for the summary.
  const pricingBreakdown = useMemo(() => {
    const bd = {};
    for (const key of selectedCells) {
      const [gid,sid] = key.split('::');
      const ground = locationGrounds.find(g=>g.id===gid);
      const gName  = ground?.name || gid;
      if (!bd[gName]) bd[gName] = { count: 0, total: 0 };
      bd[gName].count += 1;
      bd[gName].total += getSlotPrice(gid, sid);
    }
    return bd;
  }, [selectedCells, getSlotPrice, locationGrounds]);

  const grandBookingTotal = courtTotal + rentalTotal;

  // ── Payment ──────────────────────────────────────────────────────────────
  const [payment, setPayment] = useState({ method: 'Cash', amount: '', status: 'Pending', transaction_id: '' });

  // ── Submit ───────────────────────────────────────────────────────────────
  const [progress, setProgress] = useState(null);

  // Recurring: create a booking for every FREE occurrence of each selected slot.
  const handleRecurringSubmit = async () => {
    const keys = Object.keys(recSelected);
    if (keys.length === 0) { toast.error('Select at least one recurring slot'); return; }
    if (!form.customer_name.trim() || !form.customer_phone.trim()) { toast.error('Enter customer name and phone'); return; }

    const tasks = [];
    for (const key of keys) {
      const sl = recurringSlots.find(x => x.key === key);
      if (!sl) continue;
      const slotTime = sl.label || sl.sid;
      for (const d of recAvail.occurrences) {
        if (recAvail.booked.has(`${d}|${sl.ground.id}|${sl.sid}`)) continue;
        tasks.push({ date: d, ground_id: sl.ground.id, slot_id: sl.sid, slot_time: slotTime });
      }
    }
    if (tasks.length === 0) { toast.error('No free sessions to create'); return; }

    const sessionId = crypto.randomUUID();
    setProgress({ done: 0, total: tasks.length });
    let success = 0, conflict = 0, done = 0;
    for (const batch of chunk(tasks, 8)) {
      const results = await Promise.all(batch.map(t => api.post('/api/bookings/', {
        location_id: form.location_id, ground_id: t.ground_id, date: t.date,
        slot_id: t.slot_id, slot_time: t.slot_time,
        customer_name: form.customer_name.trim(), customer_phone: form.customer_phone.trim(),
        notes: form.notes || null, payment: null, rentals: null, session_id: sessionId,
      }).then(() => 'ok').catch(err => err.response?.status === 409 ? 'conflict' : 'error')));
      success  += results.filter(r => r === 'ok').length;
      conflict += results.filter(r => r === 'conflict').length;
      done     += batch.length;
      setProgress({ done, total: tasks.length });
    }
    setProgress(null);
    if (success > 0) {
      toast.success(`${success} session${success>1?'s':''} created`);
      if (conflict === 0) navigate('/admin/bookings');
      else setRecSelected({});
    }
    if (conflict > 0) toast.error(`${conflict} slot${conflict>1?'s':''} were already booked and skipped`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (bookingMode === 'recurring') return handleRecurringSubmit();
    if (selectedCells.size === 0) { toast.error('Select at least one time slot'); return; }
    if (!form.customer_name.trim() || !form.customer_phone.trim()) { toast.error('Enter customer name and phone'); return; }
    if (bookingMode !== 'single' && dateTo < form.date) { toast.error('"To" date must be on or after "From" date'); return; }
    if (bookingMode === 'recurring' && weekdays.size === 0) { toast.error('Select at least one day of the week'); return; }

    const paymentData = payment.amount
      ? { method: payment.method, amount: parseFloat(payment.amount), status: payment.status,
          transaction_id: payment.method==='UPI' ? payment.transaction_id : null }
      : null;

    const rentalsData = [
      ...rentalCatalogue.filter(item=>(rentalQty[item.item_id]||0)>0).map(item=>({
        item_id: item.item_id, name: item.name,
        quantity: rentalQty[item.item_id], price_per_item: item.price_per_session,
        total: rentalQty[item.item_id]*item.price_per_session,
      })),
      ...customRentals.filter(r=>r.name.trim()&&parseFloat(r.price)>0).map(r=>({
        item_id: `custom-${r.id}`, name: r.name.trim(),
        quantity: r.qty||1, price_per_item: parseFloat(r.price), total: (r.qty||1)*parseFloat(r.price),
      })),
    ];

    const cellsToBook = [...selectedCells].map(key => {
      const [groundId,slotId]=key.split('::');
      const slotInfo=(slotsByGround[groundId]||[]).find(s=>s.id===slotId);
      return slotInfo ? {groundId,slotId,slotTime:slotInfo.time} : null;
    }).filter(Boolean);

    const sessionId = crypto.randomUUID();
    const total     = bookingDates.length * cellsToBook.length;
    setProgress({ done: 0, total });

    let successCount=0, conflictCount=0;
    const conflictedKeys = new Set();

    for (let di=0; di<bookingDates.length; di++) {
      const date = bookingDates[di];
      const slotRentalsAssigned = new Set();
      const results = await Promise.all(
        cellsToBook.map(cell => {
          const isFirst = !slotRentalsAssigned.has(cell.slotId);
          if (isFirst) slotRentalsAssigned.add(cell.slotId);
          return api.post('/api/bookings/', {
            location_id: form.location_id, ground_id: cell.groundId, date,
            slot_id: cell.slotId, slot_time: cell.slotTime,
            customer_name: form.customer_name.trim(), customer_phone: form.customer_phone.trim(),
            notes: form.notes||null, payment: paymentData,
            rentals: isFirst && rentalsData.length>0 ? rentalsData : null,
            session_id: sessionId,
          })
            .then(()=>'ok')
            .catch(err => err.response?.status===409 ? 'conflict' : 'error');
        })
      );
      successCount  += results.filter(r=>r==='ok').length;
      conflictCount += results.filter(r=>r==='conflict').length;
      if (bookingMode==='single' && results.some(r=>r==='conflict')) {
        cellsToBook.forEach((cell,idx) => { if (results[idx]==='conflict') conflictedKeys.add(`${cell.groundId}::${cell.slotId}`); });
      }
      setProgress({ done: (di+1)*cellsToBook.length, total });
    }
    setProgress(null);

    if (conflictedKeys.size > 0) {
      setSlotsByGround(prev => {
        const next={...prev};
        for (const key of conflictedKeys) { const [gid,sid]=key.split('::'); if(next[gid]) next[gid]=next[gid].map(s=>s.id===sid?{...s,available:false}:s); }
        return next;
      });
      setSelectedCells(prev => { const n=new Set(prev); conflictedKeys.forEach(k=>n.delete(k)); return n; });
    }
    if (successCount > 0) {
      toast.success(bookingMode==='single'
        ? `${successCount} booking${successCount>1?'s':''} created`
        : `${successCount} bookings created across ${bookingDates.length} date${bookingDates.length>1?'s':''}`
      );
      if (conflictCount===0) navigate('/admin/bookings');
    }
    if (conflictCount > 0) toast.error(`${conflictCount} slot${conflictCount>1?'s':''} were already booked and skipped`);
  };

  const MODES = [
    { id: 'single',    label: 'Single Day',  Icon: CalendarDays },
    { id: 'range',     label: 'Date Range',  Icon: LayoutGrid },
    { id: 'recurring', label: 'Recurring',   Icon: RefreshCw },
  ];

  const submitLabel = (() => {
    if (bookingMode==='recurring') return recSessionCount===0 ? 'Pick recurring slots' : `Confirm ${recSessionCount} session${recSessionCount>1?'s':''}`;
    if (totalBookings===0) return 'Select at least one slot';
    if (bookingMode==='single') return totalBookings===1 ? 'Create Booking' : `Create ${totalBookings} Bookings`;
    const d = bookingDates.length;
    return grandTotal===0 ? 'No matching dates' : `Create ${grandTotal} Booking${grandTotal>1?'s':''} across ${d} day${d>1?'s':''}`;
  })();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations · Bookings</div>
          <h1>New Booking</h1>
        </div>
        <div className="sb-page-actions">
          <button onClick={() => navigate('/admin/bookings')} className="sb-btn ghost">← Back</button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="l-two-col">

          {/* ── Left column ───────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Customer details */}
            <div className="sb-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h2 style={s.sec}>Customer Details</h2>

              {/* Customer search */}
              <div ref={searchRef} style={{ position: 'relative' }}>
                <span style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Search size={12} /> Search past customers
                </span>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Type name or phone to find a returning customer…"
                    style={{ ...s.inp, paddingRight: 36 }}
                  />
                  {searchLoading && (
                    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%',
                        border: '2px solid var(--line)',
                        borderTopColor: 'var(--accent)',
                        animation: 'spin 0.7s linear infinite',
                      }} />
                    </div>
                  )}
                </div>
                {showSuggestions && suggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', zIndex: 20, width: '100%', marginTop: 4,
                    background: 'var(--surface)', border: '1px solid var(--line)',
                    borderRadius: 10, boxShadow: 'var(--sh-2)', overflow: 'hidden',
                  }}>
                    {suggestions.map((c,i) => (
                      <button key={i} type="button" onClick={() => pickCustomer(c)} style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', background: 'none', border: 'none',
                        textAlign: 'left', cursor: 'pointer', borderBottom: i < suggestions.length-1 ? '1px solid var(--line-soft)' : 'none',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background='none'}
                      >
                        <User size={13} style={s.accent} />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</span>
                        <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 'auto', fontFamily: 'var(--f-mono)' }}>{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={s.row2}>
                <div className="sb-field">
                  <label>Name</label>
                  <input type="text" value={form.customer_name}
                    onChange={e => setForm({...form, customer_name: e.target.value})}
                    style={s.inp} required />
                </div>
                <div className="sb-field">
                  <label>Phone</label>
                  <input type="tel" value={form.customer_phone}
                    onChange={e => setForm({...form, customer_phone: e.target.value})}
                    style={s.inp} required />
                </div>
              </div>
              <div className="sb-field">
                <label>Notes <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(advance paid, special requests…)</span></label>
                <textarea rows={2} value={form.notes}
                  onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder="e.g. Advance ₹500 paid, WhatsApp confirmed"
                  style={{ ...s.inp, resize: 'none' }} />
              </div>
            </div>

            {/* Booking details */}
            <div className="sb-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Mode tabs */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="sb-chipset">
                  {MODES.map(m => (
                    <button key={m.id} type="button"
                      className={`sb-chip ${bookingMode===m.id?'active':''}`}
                      onClick={() => setBookingMode(m.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                    >
                      <m.Icon size={11} /> {m.label}
                    </button>
                  ))}
                </div>
                {totalBookings > 0 && (
                  <button type="button" onClick={() => setSelectedCells(new Set())}
                    className="sb-btn sm ghost" style={s.ink3}>
                    <X size={10} /> Clear
                  </button>
                )}
              </div>

              {/* Location + Date */}
              <div style={s.row2}>
                <div className="sb-field">
                  <label>Location</label>
                  <select value={form.location_id??''} onChange={e=>setForm({...form,location_id:parseInt(e.target.value)})} style={s.inp}>
                    {allowedLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
                  <div className="sb-field">
                    <label>{bookingMode==='single' ? 'Date' : 'From'}</label>
                    <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} style={s.inp} />
                  </div>
                  {bookingMode !== 'single' && (
                    <div className="sb-field">
                      <label>To</label>
                      <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={s.inp} />
                    </div>
                  )}
                </div>
              </div>

              {/* Recurring weekdays */}
              {bookingMode==='recurring' && (
                <div className="sb-field">
                  <label>Repeat on days</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
                    {WD_LABELS.map((label,i) => (
                      <button key={i} type="button" onClick={()=>toggleWeekday(i)}
                        style={{
                          padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                          border: `1px solid ${weekdays.has(i)?'transparent':'var(--line)'}`,
                          background: weekdays.has(i) ? 'var(--accent)' : 'transparent',
                          color: weekdays.has(i) ? 'var(--accent-ink)' : 'var(--ink-2)',
                          cursor: 'pointer', transition: 'all .1s',
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date range preview */}
              {bookingMode !== 'single' && (
                <div style={{
                  background: 'var(--surface-2)', border: '1px solid var(--line-soft)',
                  borderRadius: 9, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <CalendarDays size={13} style={s.accent} />
                  {bookingDates.length === 0 ? (
                    <p style={{ fontSize: 12, ...s.ink3, margin: 0 }}>
                      {bookingMode==='recurring'&&weekdays.size===0 ? 'Select at least one weekday' : 'No dates in range'}
                    </p>
                  ) : (
                    <p style={{ fontSize: 12, margin: 0 }}>
                      <span style={{ fontWeight: 600 }}>{bookingDates.length} date{bookingDates.length>1?'s':''}</span>
                      {' · '}
                      <span style={s.ink3}>
                        {bookingDates.length<=5
                          ? bookingDates.map(d=>fmtDate(d)).join(', ')
                          : `${fmtDate(bookingDates[0])} → ${fmtDate(bookingDates[bookingDates.length-1])}`}
                      </span>
                      {totalBookings > 0 && (
                        <span style={{ ...s.accent, fontWeight: 600, marginLeft: 8 }}>
                          = {grandTotal} total booking{grandTotal!==1?'s':''}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Single / Range: court×time grid (hidden in recurring mode) */}
              {bookingMode !== 'recurring' && (<>
              {/* Select all */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                  <LayoutGrid size={13} style={s.accent} />
                  <span style={{ fontSize: 12.5, ...s.ink3 }}>Select courts &amp; time slots — click headers to fill column / row</span>
                </div>
                {allAvailableKeys.length > 0 && (
                  <button type="button" onClick={toggleAll}
                    className={`sb-btn sm ${allAvailableSelected?'brand':'ghost'}`}>
                    <CheckSquare size={11} />
                    {allAvailableSelected ? 'Deselect All' : 'Select All Available'}
                  </button>
                )}
              </div>

              {/* Grid */}
              <div style={{ overflowX: 'auto', margin: '0 -22px', padding: '0 22px' }}>
                {slotsLoading ? (
                  <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
                    {Array.from({length:6}).map((_,i) => (
                      <div key={i} style={{ height: 32, background: 'var(--line-soft)', borderRadius: 6, opacity: 0.5 }} />
                    ))}
                  </div>
                ) : locationGrounds.length === 0 ? (
                  <p style={{ fontSize: 13, ...s.ink4, fontStyle:'italic' }}>No courts configured for this facility.</p>
                ) : (
                  <table style={{ width:'100%', minWidth:'max-content', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign:'left', fontSize:11, ...s.ink3, padding:'6px 14px 6px 0', width: 96 }}>Time</th>
                        {locationGrounds.map(g => {
                          const availInCol = (slotsByGround[g.id]||[]).filter(s=>s.available);
                          const allColOn   = availInCol.length>0 && availInCol.every(s=>selectedCells.has(`${g.id}::${s.id}`));
                          return (
                            <th key={g.id} style={{ textAlign:'center', padding:'4px 4px' }}>
                              <button type="button" onClick={()=>toggleColumn(g.id)}
                                style={{
                                  padding: '4px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                                  border: 'none', cursor: 'pointer', transition: 'all .1s',
                                  background: allColOn ? 'var(--accent)' : 'transparent',
                                  color: allColOn ? 'var(--accent-ink)' : 'var(--ink-2)',
                                }}>
                                {g.name}
                                <span style={{ display:'block', fontSize:10, fontWeight:400, color: allColOn?'var(--accent-ink)':'var(--ink-4)', letterSpacing:'.06em', textTransform:'uppercase' }}>{g.sport}</span>
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {allTimeSlots.map((slot, rowIdx) => {
                        const availInRow = locationGrounds.filter(g=>(slotsByGround[g.id]||[]).find(s=>s.id===slot.id)?.available);
                        const allRowOn   = availInRow.length>0 && availInRow.every(g=>selectedCells.has(`${g.id}::${slot.id}`));
                        return (
                          <tr key={slot.id} style={{ background: rowIdx%2===0?'var(--surface-2)':'transparent' }}>
                            <td style={{ padding:'3px 14px 3px 0', whiteSpace:'nowrap' }}>
                              <button type="button" onClick={()=>toggleRow(slot.id)}
                                style={{
                                  fontSize: 11.5, padding: '2px 4px', borderRadius: 5, border:'none',
                                  background:'transparent', cursor: isSlotPast(slot.id)?'default':'pointer',
                                  color: isSlotPast(slot.id)?'var(--line)': allRowOn?'var(--accent)': availInRow.length>0?'var(--ink-2)':'var(--line)',
                                  fontFamily:'var(--f-mono)', textDecoration: isSlotPast(slot.id)?'line-through':'none',
                                  fontWeight: allRowOn?600:400,
                                }}>
                                {slot.time}
                              </button>
                            </td>
                            {locationGrounds.map(g => {
                              const slotInfo = (slotsByGround[g.id]||[]).find(s=>s.id===slot.id);
                              const cellKey  = `${g.id}::${slot.id}`;
                              const isSel    = selectedCells.has(cellKey);
                              const isAvail  = slotInfo?.available ?? false;
                              const isPast   = isSlotPast(slot.id);
                              const info     = slotInfo?.booking_info;
                              // This ground doesn't offer this slot (different custom slot list).
                              if (!slotInfo) {
                                return (
                                  <td key={g.id} style={{ padding:'3px 4px', textAlign:'center' }}>
                                    <div style={{ width:'100%', minWidth:72, padding:'5px 6px', fontSize:11.5, color:'var(--line)' }}>·</div>
                                  </td>
                                );
                              }
                              return (
                                <td key={g.id} style={{ padding:'3px 4px', textAlign:'center' }}>
                                  <div style={{ position:'relative', display:'inline-block', width:'100%' }} className="group">
                                    <button type="button"
                                      disabled={!isAvail || isPast}
                                      onClick={() => !isPast && toggleCell(g.id, slot.id)}
                                      style={{
                                        width:'100%', minWidth:72, padding:'5px 6px', borderRadius:7,
                                        fontSize:11.5, fontWeight:500, cursor: (!isAvail||isPast)?'not-allowed':'pointer',
                                        border:'none', transition:'all .08s',
                                        background: isPast&&!isAvail ? 'color-mix(in oklch,oklch(0.7 0.09 60) 15%,transparent)'
                                          : isPast           ? 'var(--surface-2)'
                                          : !isAvail         ? 'color-mix(in oklch, var(--bad-fg) 10%, transparent)'
                                          : isSel            ? 'var(--accent)'
                                          : 'var(--surface-2)',
                                        color: isPast&&!isAvail ? 'oklch(0.65 0.08 60)'
                                          : isPast           ? 'var(--line)'
                                          : !isAvail         ? 'var(--bad-fg)'
                                          : isSel            ? 'var(--accent-ink)'
                                          : 'var(--ink-3)',
                                        textDecoration: isPast&&!isAvail?'none': isPast?'line-through':'none',
                                      }}>
                                      {isPast&&!isAvail?'Booked': isPast?'—': !isAvail?'Booked': isSel?'✓':'○'}
                                    </button>
                                    {!isAvail && info && (
                                      <div style={{
                                        position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)',
                                        zIndex:30, display:'none', pointerEvents:'none',
                                        background:'var(--surface)', border:'1px solid var(--line)',
                                        borderRadius:9, padding:'8px 12px', boxShadow:'var(--sh-2)', whiteSpace:'nowrap',
                                      }} className="sb-tooltip">
                                        <p style={{ fontSize:12, fontWeight:600, margin:0 }}>{info.customer_name}</p>
                                        {info.customer_phone && <p style={{ fontSize:11, color:'var(--ink-3)', margin:0 }}>{info.customer_phone}</p>}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              </>)}

              {/* Recurring: Step 3 (time of day) + Step 4 (availability-checked slots) */}
              {bookingMode === 'recurring' && (
                <div style={{ display:'flex', flexDirection:'column', gap: 18 }}>
                  {/* Step 3 — Time of day */}
                  <div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>Step 3 · Time of day</span>
                      <span style={{ fontSize: 11.5, ...s.ink3 }}>{timeWindows.length===0 ? 'Pick at least one' : `${timeWindows.length} window${timeWindows.length===1?'':'s'}`}</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap: 10 }}>
                      {Object.keys(TIME_WINDOWS).map(k => {
                        const w = TIME_WINDOWS[k]; const active = timeWindows.includes(k);
                        return (
                          <button key={k} type="button" onClick={()=>toggleWindow(k)}
                            style={{ padding:'14px', borderRadius:12, textAlign:'left', cursor:'pointer',
                              border:`1px solid ${active?'transparent':'var(--line)'}`,
                              background: active?'var(--brand)':'var(--surface)',
                              color: active?'var(--brand-ink)':'var(--ink)',
                              display:'flex', flexDirection:'column', gap:4 }}>
                            <span style={{ fontSize:14, fontWeight:600 }}>{w.label}</span>
                            <span className="sb-mono" style={{ fontSize:11, opacity: active?0.9:0.6 }}>{w.range}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Step 4 — Available recurring slots */}
                  <div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8, flexWrap:'wrap', gap:8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>Step 4 · Available recurring slots</span>
                      <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
                        {recReady && <span className="sb-badge ok">{fullyFreeCount} fully open</span>}
                        {recReady && (
                          <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'var(--ink-2)', cursor:'pointer' }}>
                            <input type="checkbox" checked={showFreeOnly} onChange={e=>setShowFreeOnly(e.target.checked)} style={{ accentColor:'var(--brand)' }} />
                            Free only
                          </label>
                        )}
                        {Object.keys(recSelected).length>0 && (
                          <button type="button" className="sb-btn ghost sm" onClick={()=>setRecSelected({})}>Clear</button>
                        )}
                      </div>
                    </div>

                    {!recReady ? (
                      <div style={{ padding:'40px 24px', textAlign:'center', ...s.ink3, fontSize:13, border:'1px dashed var(--line)', borderRadius:12 }}>
                        {exceedsLimit ? 'Range exceeds 1 year — shorten the end date.'
                         : weekdays.size===0 ? 'Pick at least one weekday above.'
                         : timeWindows.length===0 ? 'Pick a time of day.'
                         : recLoading ? 'Loading availability…'
                         : 'Set a date range with at least one matching date.'}
                      </div>
                    ) : visibleRecSlots.length===0 ? (
                      <div style={{ padding:'32px 24px', textAlign:'center', ...s.ink3, fontSize:13 }}>
                        No slots available for this combination. Try widening the time window or weekdays.
                      </div>
                    ) : (
                      <div style={{ border:'1px solid var(--line)', borderRadius:12, overflow:'hidden' }}>
                        {locationGrounds.map(ground => {
                          const courtSlots = visibleRecSlots.filter(sl=>sl.ground.id===ground.id);
                          if (courtSlots.length===0) return null;
                          return (
                            <div key={ground.id}>
                              <div style={{ padding:'9px 14px', background:'var(--surface-2)', borderBottom:'1px solid var(--line-soft)', display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                                <div>
                                  <span style={{ fontWeight:600, fontSize:13 }}>{ground.name}</span>
                                  {ground.sport && <span style={{ ...s.ink3, fontSize:11, marginLeft:6, letterSpacing:'.08em', textTransform:'uppercase' }}>{ground.sport}</span>}
                                </div>
                              </div>
                              {courtSlots.map(sl => {
                                const sel = !!recSelected[sl.key];
                                const fully = sl.free===sl.total, partial = sl.free>0&&sl.free<sl.total, empty = sl.free===0;
                                const color = fully?'var(--ok-fg)':partial?'var(--warn-fg)':'var(--bad-fg)';
                                return (
                                  <div key={sl.key} onClick={()=>!empty && toggleRecSlot(sl.key)}
                                    style={{ display:'grid', gridTemplateColumns:'20px 150px 1fr auto', gap:12, alignItems:'center', padding:'10px 14px', borderBottom:'1px solid var(--line-soft)', cursor: empty?'not-allowed':'pointer', background: sel?'var(--surface-2)':'transparent', opacity: empty?0.45:1 }}>
                                    <div style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${sel?'var(--brand)':'var(--line)'}`, background: sel?'var(--brand)':'var(--surface)', display:'grid', placeItems:'center', color:'var(--brand-ink)', fontSize:11 }}>{sel?'✓':''}</div>
                                    <div className="sb-mono" style={{ fontSize:12.5, fontWeight:500 }}>
                                      {sl.label}
                                      {sl.price>0 && <span style={{ ...s.ink3, marginLeft:6 }}>₹{sl.price}</span>}
                                    </div>
                                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                      <div className="sb-bar" style={{ width:80 }}><i style={{ width:`${(sl.free/sl.total)*100}%`, background: color }} /></div>
                                      <span className="sb-mono" style={{ fontSize:12, color }}>{sl.free} / {sl.total}</span>
                                      <span style={{ fontSize:11.5, ...s.ink3 }}>{fully?'all dates free':partial?'some conflicts':'fully booked'}</span>
                                    </div>
                                    <span className="sb-mono sb-num" style={{ fontSize:12.5, ...s.ink3 }}>₹{(sl.price*sl.free).toLocaleString('en-IN')}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Equipment rentals */}
            <div className="sb-card" style={{ padding: '20px 22px' }}>
              <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 16 }}>
                <ShoppingBag size={15} style={s.accent} />
                <h2 style={s.sec}>Equipment Rentals <span style={{ color:'var(--ink-3)', fontWeight:400, fontSize:13 }}>(optional)</span></h2>
              </div>

              {rentalCatalogue.length > 0 ? (
                <div style={{ display:'flex', flexDirection:'column', gap: 10, marginBottom: 12 }}>
                  {rentalCatalogue.map(item => {
                    const avail      = effectiveStock(item);
                    const currentQty = Math.min(rentalQty[item.item_id]||0, avail);
                    const lineTotal  = currentQty * item.price_per_session * hrs;
                    const perHourData = uniqueSlotIds.map(sid => {
                      const id=String(item.item_id), bucket=rentalUsage[sid]||{};
                      const used=bucket[id]??bucket[item.item_id]??0;
                      const slotAvail=Math.max(0,(item.quantity??0)-used);
                      const slotInfo=allTimeSlots.find(s=>s.id===sid);
                      return {sid, slotAvail, startLabel:slotInfo?.start_time??sid};
                    }).sort((a,b)=>a.sid.localeCompare(b.sid));
                    const hasConstraint   = perHourData.some(h=>h.slotAvail<(item.quantity??0));
                    const bottleneckAvail = avail;

                    return (
                      <div key={item.item_id} style={{ background:'var(--surface-2)', border:'1px solid var(--line-soft)', borderRadius:10, padding:'12px 14px', display:'flex', flexDirection:'column', gap: 10 }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span style={{ fontSize:13.5, fontWeight:600 }}>{item.name}</span>
                          <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
                            <button type="button" onClick={()=>changeRentalQty(item.item_id,-1,avail)} disabled={currentQty===0}
                              className="sb-btn sm ghost icon" style={{ borderRadius:'50%', width:28, height:28, padding:0 }}>
                              <Minus size={11} />
                            </button>
                            <span style={{ fontFamily:'var(--f-mono)', fontWeight:600, fontSize:14, minWidth:20, textAlign:'center' }}>{currentQty}</span>
                            <button type="button" onClick={()=>changeRentalQty(item.item_id,1,avail)} disabled={currentQty>=avail}
                              className="sb-btn sm ghost icon" style={{ borderRadius:'50%', width:28, height:28, padding:0 }}>
                              <Plus size={11} />
                            </button>
                          </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap', fontSize: 12 }}>
                          <span style={s.accent}>₹{item.price_per_session}/hr</span>
                          <span style={s.ink4}>·</span>
                          <span style={{ color: avail===0?'var(--bad-fg)':avail<=3?'var(--warn-fg)':'var(--ink-3)' }}>
                            {avail===0 ? 'None available'
                              : uniqueSlotIds.length>0&&hasConstraint ? `${avail} of ${item.quantity} effective (worst hour)`
                              : `${avail} of ${item.quantity} available`}
                          </span>
                          {currentQty > 0 && lineTotal > 0 && <span style={s.ink3}>= ₹{lineTotal}</span>}
                        </div>
                        {uniqueSlotIds.length > 0 && (
                          <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
                            {perHourData.map(({sid, slotAvail, startLabel}) => {
                              const isEmpty=slotAvail===0, isLow=!isEmpty&&slotAvail<=3;
                              const isBot=slotAvail===bottleneckAvail&&hasConstraint&&!isEmpty;
                              const pct=item.quantity>0?slotAvail/item.quantity:1;
                              return (
                                <div key={sid} style={{
                                  display:'flex', flexDirection:'column', alignItems:'center',
                                  padding:'6px 10px', borderRadius:9, minWidth:56,
                                  border: `1px solid ${isEmpty?'var(--bad-fg)':isBot?'var(--accent)':isLow?'oklch(0.7 0.1 50)':'var(--line)'}`,
                                  background: isEmpty?'color-mix(in oklch,var(--bad-fg) 8%,transparent)':isBot?'color-mix(in oklch,var(--accent) 8%,transparent)':'var(--surface)',
                                  fontSize:11,
                                }}>
                                  <span style={{ fontWeight:600, fontFamily:'var(--f-mono)', color:isEmpty?'var(--bad-fg)':isBot?'var(--accent)':isLow?'oklch(0.6 0.12 50)':'var(--ink-2)' }}>{startLabel}</span>
                                  <div style={{ width:'100%', height:3, borderRadius:999, background:'var(--line-soft)', margin:'4px 0 2px' }}>
                                    <div style={{ height:3, borderRadius:999, width:`${Math.round(pct*100)}%`, background:isEmpty?'var(--bad-fg)':isLow||isBot?'var(--accent)':'oklch(0.65 0.16 150)' }} />
                                  </div>
                                  <span style={{ color:isEmpty?'var(--bad-fg)':isBot?'var(--accent)':isLow?'oklch(0.6 0.12 50)':'var(--ink-3)' }}>{isEmpty?'Full':`${slotAvail}/${item.quantity}`}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ fontSize:12, ...s.ink4, fontStyle:'italic', marginBottom:10 }}>
                  No rental catalogue for this facility — add items in Facilities → Edit, or add one-off items below.
                </p>
              )}

              {customRentals.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap: 8, marginBottom: 10 }}>
                  {customRentals.map(r => (
                    <div key={r.id} style={{ display:'flex', alignItems:'center', gap: 8 }}>
                      <input type="text" value={r.name} onChange={e=>updateCustomRental(r.id,'name',e.target.value)}
                        placeholder="Item name" style={{ ...s.inp, padding:'6px 10px', fontSize:13 }} />
                      <div style={{ display:'flex', alignItems:'center', gap:4, background:'var(--surface-2)', border:'1px solid var(--line)', borderRadius:8, padding:'6px 10px' }}>
                        <span style={{ ...s.ink3, fontSize:13 }}>₹</span>
                        <input type="number" min="0" value={r.price} onChange={e=>updateCustomRental(r.id,'price',e.target.value)}
                          placeholder="0" style={{ width:56, background:'transparent', border:'none', outline:'none', fontSize:13, color:'var(--ink)' }} />
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <button type="button" onClick={()=>updateCustomRental(r.id,'qty',Math.max(1,(r.qty||1)-1))} className="sb-btn sm ghost icon" style={{ borderRadius:'50%', width:24, height:24, padding:0 }}><Minus size={10}/></button>
                        <span style={{ fontFamily:'var(--f-mono)', fontSize:13, width:16, textAlign:'center' }}>{r.qty||1}</span>
                        <button type="button" onClick={()=>updateCustomRental(r.id,'qty',(r.qty||1)+1)} className="sb-btn sm ghost icon" style={{ borderRadius:'50%', width:24, height:24, padding:0 }}><Plus size={10}/></button>
                      </div>
                      <button type="button" onClick={()=>removeCustomRental(r.id)} className="sb-btn sm ghost icon" style={{ color:'var(--bad-fg)' }}><X size={13}/></button>
                    </div>
                  ))}
                </div>
              )}

              <button type="button" onClick={addCustomRental} className="sb-btn sm ghost" style={s.brand}>
                <Plus size={11} /> Add one-off rental item
              </button>
            </div>
          </div>

          {/* ── Right column — sticky summary ─────────────────────────────── */}
          <div className="l-aside-sticky" style={{ display:'flex', flexDirection:'column', gap: 16 }}>

            {/* Selection summary */}
            <div className="sb-card" style={{ padding: '18px 20px' }}>
              <div className="sb-card-lbl" style={{ marginBottom: 12 }}>Booking Summary</div>

              {bookingMode === 'recurring' ? (
                Object.keys(recSelected).length === 0 ? (
                  <p style={{ fontSize:13, ...s.ink4, fontStyle:'italic' }}>Pick recurring slots in Step 4 to see a summary.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                    <div style={{ padding:'10px 12px', background:'color-mix(in oklch, var(--accent) 8%, transparent)', border:'1px solid color-mix(in oklch, var(--accent) 20%, transparent)', borderRadius:9 }}>
                      <p style={{ fontSize:13, fontWeight:600, ...s.accent, margin:0 }}>{recSessionCount} session{recSessionCount!==1?'s':''}</p>
                      <p style={{ fontSize:11, ...s.ink3, margin:'4px 0 0', fontFamily:'var(--f-mono)' }}>
                        {Object.keys(recSelected).length} slot{Object.keys(recSelected).length!==1?'s':''} · Every {[...weekdays].sort().map(d=>WD_LABELS[d]).join(', ')}
                      </p>
                    </div>
                    <div style={{ borderTop:'1px solid var(--line-soft)', paddingTop:10, display:'flex', flexDirection:'column', gap:4 }}>
                      {Object.keys(recSelected).map(k => {
                        const sl = recurringSlots.find(x=>x.key===k); if (!sl) return null;
                        return (
                          <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                            <span style={s.ink3}>{sl.ground.name} · {sl.label} × {sl.free}</span>
                            <span style={{ fontFamily:'var(--f-mono)' }}>₹{(sl.price*sl.free).toLocaleString('en-IN')}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:600, paddingTop:4, borderTop:'1px solid var(--line-soft)' }}>
                      <span>Total to collect</span>
                      <span style={{ ...s.accent, fontFamily:'var(--f-mono)' }}>₹{recCourtSubtotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                )
              ) : totalBookings === 0 ? (
                <p style={{ fontSize:13, ...s.ink4, fontStyle:'italic' }}>Select slots from the grid to see a summary.</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                  {/* Counts */}
                  <div style={{
                    padding: '10px 12px',
                    background: 'color-mix(in oklch, var(--accent) 8%, transparent)',
                    border: '1px solid color-mix(in oklch, var(--accent) 20%, transparent)',
                    borderRadius: 9,
                  }}>
                    <p style={{ fontSize:13, fontWeight:600, ...s.accent, margin:0 }}>
                      {bookingMode==='single'
                        ? `${totalBookings} slot${totalBookings>1?'s':''} selected`
                        : `${totalBookings} slot${totalBookings>1?'s':''} × ${bookingDates.length} day${bookingDates.length>1?'s':''} = ${grandTotal} bookings`}
                    </p>
                    {bookingMode!=='single' && bookingDates.length > 0 && (
                      <p style={{ fontSize:11, ...s.ink3, margin:'4px 0 0', fontFamily:'var(--f-mono)' }}>
                        {bookingMode==='recurring'
                          ? `Every ${[...weekdays].sort().map(d=>WD_LABELS[d]).join(', ')}`
                          : 'Daily'}
                        {' · '}{fmtDate(bookingDates[0])} → {fmtDate(bookingDates[bookingDates.length-1])}
                      </p>
                    )}
                  </div>

                  {/* Per-ground breakdown */}
                  {Object.entries(selectionByGround).map(([gid, slotIds]) => {
                    const ground = locationGrounds.find(g=>g.id===gid);
                    const slots  = (slotsByGround[gid]||[]).filter(s=>slotIds.includes(s.id))
                      .sort((a,b)=>(a.start_time||'').localeCompare(b.start_time||''));
                    const h = slots.length;
                    return (
                      <div key={gid} style={{ fontSize:12, display:'flex', justifyContent:'space-between', gap: 8 }}>
                        <span style={{ fontWeight:500 }}>{ground?.name||gid}</span>
                        <span style={s.ink3}>
                          {h===1 ? slots[0].label : `${slots[0]?.start_time} → ${slots[h-1]?.end_time} (${h} slots)`}
                        </span>
                      </div>
                    );
                  })}

                  {/* Pricing breakdown */}
                  {courtTotal > 0 && (
                    <div style={{ borderTop:'1px solid var(--line-soft)', paddingTop: 10, display:'flex', flexDirection:'column', gap: 4 }}>
                      {Object.entries(pricingBreakdown).filter(([,info])=>info.total>0).map(([gName,info]) => (
                        <div key={gName} style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                          <span style={s.ink3}>{gName} · {info.count} slot{info.count!==1?'s':''}</span>
                          <span style={{ fontFamily:'var(--f-mono)' }}>₹{info.total}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Totals */}
                  {(courtTotal > 0 || rentalTotal > 0) && (
                    <div style={{ borderTop:'1px solid var(--line-soft)', paddingTop: 10, display:'flex', flexDirection:'column', gap: 6 }}>
                      {courtTotal > 0 && (
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                          <span style={s.ink3}>Court charges</span>
                          <span style={{ fontFamily:'var(--f-mono)' }}>₹{courtTotal}</span>
                        </div>
                      )}
                      {rentalTotal > 0 && (
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                          <span style={s.ink3}>Equipment rentals{rentalHours>1?` (${rentalHours}hr)`:''}</span>
                          <span style={{ fontFamily:'var(--f-mono)' }}>₹{rentalTotal}</span>
                        </div>
                      )}
                      {(courtTotal > 0 || rentalTotal > 0) && (
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:15, fontWeight:600, paddingTop:4, borderTop:'1px solid var(--line-soft)' }}>
                          <span>Total to collect</span>
                          <span style={{ ...s.accent, fontFamily:'var(--f-mono)' }}>₹{grandBookingTotal}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment */}
            <div className="sb-card" style={{ padding: '18px 20px', display:'flex', flexDirection:'column', gap: 14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div className="sb-card-lbl" style={{ margin:0 }}>Payment</div>
                <span style={{ fontSize:11, ...s.ink3 }}>optional — can record later</span>
              </div>

              {grandBookingTotal > 0 && (
                <div style={{ display:'flex', gap: 6 }}>
                  <button type="button"
                    onClick={() => setPayment(p=>({...p,amount:String(grandBookingTotal),status:'Paid'}))}
                    style={{
                      flex:1, padding:'8px 10px', fontSize:11.5, fontWeight:600, borderRadius:8,
                      background:'color-mix(in oklch, var(--ok-fg) 10%, transparent)',
                      border:'1px solid color-mix(in oklch, var(--ok-fg) 25%, transparent)',
                      color:'var(--ok-fg)', cursor:'pointer',
                    }}>
                    ✓ Full · ₹{grandBookingTotal}
                  </button>
                  <button type="button"
                    onClick={() => setPayment(p=>({...p,amount:'',status:'Pending'}))}
                    className="sb-btn sm ghost">
                    Pending
                  </button>
                </div>
              )}

              <div style={s.row2}>
                <div className="sb-field">
                  <label>Method</label>
                  <div style={{ display:'flex', gap: 6 }}>
                    {['Cash','UPI'].map(m => (
                      <button key={m} type="button" onClick={()=>setPayment({...payment,method:m})}
                        style={{
                          flex:1, padding:'7px 10px', borderRadius:8, fontSize:13, fontWeight:500,
                          border:`1px solid ${payment.method===m?'var(--accent)':'var(--line)'}`,
                          background: payment.method===m?'var(--accent)':'var(--surface-2)',
                          color: payment.method===m?'var(--accent-ink)':'var(--ink-2)',
                          cursor:'pointer',
                        }}>{m}</button>
                    ))}
                  </div>
                </div>
                <div className="sb-field">
                  <label>Amount (₹)</label>
                  <input type="number" value={payment.amount}
                    onChange={e=>setPayment({...payment,amount:e.target.value})}
                    style={s.inp} placeholder="Leave blank if not yet" />
                </div>
              </div>

              {payment.amount && (
                <div style={s.row2}>
                  <div className="sb-field">
                    <label>Status</label>
                    <select value={payment.status} onChange={e=>setPayment({...payment,status:e.target.value})} style={s.inp}>
                      <option>Pending</option><option>Paid</option><option>Refunded</option>
                    </select>
                  </div>
                  {payment.method==='UPI' && (
                    <div className="sb-field">
                      <label>UPI Txn ID</label>
                      <input type="text" value={payment.transaction_id}
                        onChange={e=>setPayment({...payment,transaction_id:e.target.value})} style={s.inp} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit */}
            {progress ? (
              <div style={{ padding: '8px 0' }}>
                <div style={{ background:'var(--line-soft)', borderRadius:999, height:4, overflow:'hidden', marginBottom:8 }}>
                  <div style={{ background:'var(--accent)', height:4, borderRadius:999, transition:'width .3s', width:`${Math.round((progress.done/progress.total)*100)}%` }} />
                </div>
                <p style={{ textAlign:'center', fontSize:13, ...s.ink3 }}>Creating bookings… {progress.done} / {progress.total}</p>
              </div>
            ) : (
              <button type="submit" disabled={bookingMode==='recurring' ? recSessionCount===0 : (totalBookings===0||grandTotal===0)} className="sb-btn primary" style={{ width:'100%', justifyContent:'center', padding:'11px 16px', fontSize:14 }}>
                {submitLabel}
              </button>
            )}
          </div>
        </div>
      </form>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .group:hover .sb-tooltip { display: block !important; }`}</style>
    </div>
  );
}
